import { type LucideIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { AnimalAvatar } from "@/components/animals/animal-avatar";
import { FollowAnimalButton } from "@/components/follow-animal-button";
import { formatSpeciesLabel } from "@/lib/score-utils";

interface AnimalSummaryHeaderProps {
  animalId: string;
  name: string | null;
  species: string | null;
  photoUrl: string | null;
  isLoading: boolean;
  onClose: () => void;
  showFollowButton?: boolean;
  drawerIcon?: LucideIcon;
  drawerLabel?: string;
}

export function AnimalSummaryHeader({
  animalId,
  name,
  species,
  photoUrl,
  isLoading,
  onClose,
  showFollowButton = false,
  drawerIcon: DrawerIcon,
  drawerLabel,
}: AnimalSummaryHeaderProps) {
  const hasTwoRows = !!(DrawerIcon || drawerLabel);

  return (
    <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b px-4 py-3">
      {hasTwoRows && (
        <div className="flex items-center gap-2 mb-2">
          {DrawerIcon && <DrawerIcon className="h-4 w-4 text-muted-foreground" />}
          {drawerLabel && (
            <span className="text-sm font-medium text-muted-foreground">{drawerLabel}</span>
          )}
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="icon"
            className="flex-shrink-0"
            onClick={onClose}
            data-testid="animal-summary-header-close"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
        </div>
      )}

      <div className="flex items-center gap-2">
        <AnimalAvatar
          animalId={animalId}
          name={name ?? animalId}
          species={species ?? ""}
          photoUrl={photoUrl}
          className="h-7 w-7"
        />

        {name !== null ? (
          <span className="font-semibold truncate">{name}</span>
        ) : isLoading ? (
          <Skeleton className="h-5 w-32" />
        ) : (
          <span className="font-semibold">{animalId}</span>
        )}

        {species !== null ? (
          <Badge variant="outline" className="text-xs shrink-0" data-testid="badge-species-summary-header">
            {formatSpeciesLabel(species)}
          </Badge>
        ) : isLoading ? (
          <Skeleton className="h-4 w-16" />
        ) : null}

        <span className="text-muted-foreground font-normal shrink-0">({animalId})</span>

        <div className="flex-1" />

        {showFollowButton && <FollowAnimalButton animalId={animalId} />}

        {!hasTwoRows && (
          <Button
            variant="ghost"
            size="icon"
            className="flex-shrink-0"
            onClick={onClose}
            data-testid="animal-summary-header-close"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
        )}
      </div>
    </div>
  );
}
